import type {
  EchoflicksSessionEvent,
  EchoflicksSessionMedia,
} from "../types.js";
import {
  boundaryLabel,
  readNarrationWindows,
  windowBySceneId,
  type NarrationWindow,
} from "./narrationWindows.js";

// ---------------------------------------------------------------------------
// Typed view over the director-script envelope (loaded via useScriptEnvelope).
// These helpers turn the raw envelope into PM-readable "story" data: the title
// decision, the storyboard (prompt + narration per shot), and short summaries
// of the voice and music choices. Loading stays in `useScriptEnvelope`.
// ---------------------------------------------------------------------------

export type ScriptEnvelope = {
  style?: unknown;
  diagnostics?: ScriptDiagnostics;
  script?: DirectorScript;
  [key: string]: unknown;
};

export type ScriptDiagnostics = {
  title_card?: TitleCardDiagnostics;
  [key: string]: unknown;
};

export type TitleCardDiagnostics = {
  guidance?: {
    preferred?: unknown;
    rationale?: unknown;
    [key: string]: unknown;
  };
  selection?: {
    selected_card_types?: unknown;
    followed_recommendation?: unknown;
    matched_preferred?: unknown;
    non_recommended_selected?: unknown;
    llm_decision_rationale?: unknown;
    mismatch_reason?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type DirectorScript = {
  title?: string;
  description?: string;
  video_content?: ScriptScene[];
  audio_content?: AudioCue[];
  music_prompt?: MusicPrompt;
  voice_generation?: VoiceGeneration;
  aspect_ratio?: string;
  filter?: string | null;
  [key: string]: unknown;
};

export type ScriptScene = {
  id?: number;
  type?: string;
  video_prompt?: ScenePrompt;
  transition?: string;
  [key: string]: unknown;
};

export type ScenePrompt = {
  file?: string;
  length?: number;
  prompt?: string | null;
  generation_prompt?: string | null;
  cuts?: number[];
  card_type?: string;
  text?: string;
  title_card_text?: string;
  title_card_bg?: string;
  [key: string]: unknown;
};

export type AudioCue = {
  start_second?: number;
  voice_over_prompt?: string | null;
  /** NSB narration-scene binding: recommended window id the writer selected. */
  window_id?: number | null;
  /**
   * Orchestrator-authored scene-anchored binding receipt
   * (anchor_scene_id, scene_ids, mode, source, shift_seconds, …).
   */
  binding?: Record<string, unknown> | null;
  [key: string]: unknown;
};

export type MusicPrompt = {
  composition_plan?: {
    positive_global_styles?: string[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type VoiceGeneration = {
  gen_type?: string | null;
  voice_id?: string | null;
  [key: string]: unknown;
};

// The loader (useScriptEnvelope) returns the envelope untyped, so the public
// helpers accept the loose shape and narrow internally.
type EnvelopeInput = Record<string, unknown> | undefined;

export function getDirectorScript(envelope: ScriptEnvelope): DirectorScript {
  return envelope.script ?? (envelope as DirectorScript);
}

export function directorScriptOf(
  envelope: EnvelopeInput,
): DirectorScript | undefined {
  return envelope ? getDirectorScript(envelope as ScriptEnvelope) : undefined;
}

function getTitleCardDiagnostics(
  diagnostics: ScriptDiagnostics | undefined,
): TitleCardDiagnostics | null {
  if (!isRecord(diagnostics?.title_card)) return null;
  return diagnostics.title_card as TitleCardDiagnostics;
}

function buildMediaMap(media: EchoflicksSessionMedia[]) {
  const byFilename = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const filename =
      item.s3_key?.split("/").at(-1) ?? item.local_path?.split(/[\\/]/).at(-1);
    if (filename && !byFilename.has(filename)) {
      byFilename.set(filename, item);
    }
  }
  return byFilename;
}

export function durationForScene(scene: ScriptScene) {
  const prompt = scene.video_prompt;
  if (!prompt) return null;
  if (typeof prompt.length === "number") return prompt.length;
  if (Array.isArray(prompt.cuts) && prompt.cuts.length >= 2) {
    return Math.max(0, Number(prompt.cuts[1]) - Number(prompt.cuts[0]));
  }
  return null;
}

export function formatSeconds(seconds?: number | null) {
  if (!seconds) return "0s";
  if (seconds < 60) return `${trimNumber(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function trimNumber(value: number) {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringList(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

function booleanValue(value: unknown) {
  return typeof value === "boolean" ? value : null;
}

// ---------------------------------------------------------------------------
// Title decision
// ---------------------------------------------------------------------------

export type TitleDecision = {
  selected: string[];
  preferred: string[];
  outsideRecommendation: string[];
  followedRecommendation: boolean | null;
  matchedPreferred: boolean | null;
  rationale: string | null;
  mismatchReason: string | null;
};

/** Distil the title-card diagnostics into a single, plain-language decision. */
export function summarizeTitleDecision(
  envelope: EnvelopeInput,
): TitleDecision | null {
  const env = envelope as ScriptEnvelope | undefined;
  const diagnostics = getTitleCardDiagnostics(env?.diagnostics);
  if (!diagnostics) return null;
  const guidance = isRecord(diagnostics.guidance) ? diagnostics.guidance : {};
  const selection = isRecord(diagnostics.selection)
    ? diagnostics.selection
    : {};
  return {
    selected: stringList(selection.selected_card_types),
    preferred: stringList(guidance.preferred),
    outsideRecommendation: stringList(selection.non_recommended_selected),
    followedRecommendation: booleanValue(selection.followed_recommendation),
    matchedPreferred: booleanValue(selection.matched_preferred),
    rationale:
      stringValue(selection.llm_decision_rationale) ??
      stringValue(guidance.rationale),
    mismatchReason: stringValue(selection.mismatch_reason),
  };
}

// ---------------------------------------------------------------------------
// Storyboard
// ---------------------------------------------------------------------------

const SCENE_KIND_LABELS: Record<string, string> = {
  TitleCardScene: "Title card",
  StaticImageScene: "Still photo",
  ImageToVideoScene: "Photo in motion",
  VideoScene: "Video clip",
};

/** Fallback per-scene duration (seconds), used only to place narration cues. */
const SCENE_FALLBACK_SECONDS = 3;

/**
 * True when the composer will animate this scene. `ImageToVideoScene` always
 * animates through the PRIMARY route, and a `StaticImageScene` with a non-empty
 * motion prompt is routed through the SECONDARY route — its declared type says
 * still, but it moves. Classifying
 * by type alone is exactly what made animate-all echoes read as slideshows.
 */
export function isAnimatedScene(scene: ScriptScene): boolean {
  if (scene.type === "ImageToVideoScene") return true;
  if (scene.type !== "StaticImageScene") return false;
  const prompt =
    scene.video_prompt?.prompt ?? scene.video_prompt?.generation_prompt;
  return typeof prompt === "string" && prompt.trim().length > 0;
}

export function sceneKindLabel(type?: string | null, animated?: boolean) {
  if (!type) return "Scene";
  if (type === "StaticImageScene" && animated)
    return "Photo in motion (secondary)";
  return (
    SCENE_KIND_LABELS[type] ?? type.replace(/Scene$/, "").replace(/_/g, " ")
  );
}

/**
 * The effective I2V audit for one scene — the real prompt that was sent to the
 * i2v provider (Kling / Veo / …), as opposed to the planner's script prompt.
 *
 * Built from (in precedence order) the durable `echo-state.v1.json` manifest's
 * `scenes[].i2v` block (full text + per-attempt trail), the
 * `scene.i2v.prompt_resolved` journey node (full text, per attempt), or the
 * MEDIA-row summary (hashes only). `source` records which one won so the UI can
 * say "summary only" when no full text is available.
 */
/** One INPUT reference image handed to the i2v provider (a start or end frame). */
export type SceneI2VReferenceFrame = {
  fileName?: string | null;
  storageKey?: string | null;
  /**
   * Where the image came from: `source_photo` / `transition_start_frame` (start),
   * `match_cut_target` / `source_loopback` (end).
   */
  role?: string | null;
};

/**
 * The uploaded first/last images the model was asked to animate *between* — the
 * true i2v reference frames, NOT the rendered clip's endpoints. `endFrame` is
 * absent for the common single-image case; present only when the shot pins an
 * end frame (a match-cut seam or the face-preservation source loopback).
 */
export type SceneI2VReferenceFrames = {
  firstFrame?: SceneI2VReferenceFrame | null;
  endFrame?: SceneI2VReferenceFrame | null;
  hasEndFrame?: boolean;
  /** Multi-reference aerial shots fill the slots with several images instead of a pair. */
  referenceFiles?: string[] | null;
};

export type SceneI2VProviderAttempt = {
  attempt?: number | null;
  provider?: string | null;
  concreteProvider?: string | null;
  model?: string | null;
  modelVersion?: string | null;
  mode?: string | null;
  lifecycleStatus?: string | null;
  qcDisposition?: string | null;
  taskId?: string | null;
  finalS3Key?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  errorCategory?: string | null;
  errorOrigin?: string | null;
  displayMessage?: string | null;
  supportCodes?: string[];
  providerHistory?: Array<{
    concreteProvider?: string | null;
    outcome?: string | null;
    errorCode?: string | null;
    retryable?: boolean;
    taskId?: string | null;
  }>;
};

export type SceneI2VAudit = {
  finalPositivePrompt?: string | null; // ← rendered as the "I2V prompt"
  negativePrompt?: string | null;
  scenePrompt?: string | null; // planner input, for the "diff" affordance
  sourcePreservationPrompt?: string | null;
  provider?: string | null;
  concreteProvider?: string | null;
  model?: string | null;
  attemptCount?: number | null;
  fallbackApplied?: boolean;
  fallbackFrom?: string | null;
  fallbackTo?: string | null;
  fallbackReason?: string | null;
  raiFiltered?: boolean;
  raiReason?: string | null;
  animated?: boolean;
  finalSceneType?: string | null;
  finalOutputKind?: string | null;
  staticFallbackApplied?: boolean;
  taskId?: string | null;
  attempts?: SceneI2VProviderAttempt[];
  referenceFrames?: SceneI2VReferenceFrames | null;
  source?: "manifest" | "journey" | "summary"; // provenance of this audit
};

/**
 * One narration line, anchored at the shot it starts over. Lines are authored
 * per multi-scene narration window (orchestrator `build_narration_window_groups`
 * groups ~2-3 shots per line), so a line usually plays across several shots —
 * the span records the anchor through the shot before the next line begins.
 */
/**
 * NWP-010: the narration window this shot belongs to, when the envelope carries
 * the real grouping. Absent on legacy envelopes, whose one-window-per-scene
 * shape is a fabrication rather than a chapter structure.
 */
export type StoryWindow = {
  id: number;
  /** True when this shot is the one the line is meant to open over. */
  isAnchor: boolean;
  /** True when this shot opens the window. */
  isFirst: boolean;
  memberCount: number;
  oversized: boolean;
  boundaryLabel: string | null;
  beatIndex: number | null;
};

export type StoryNarrationLine = {
  /** The spoken line, verbatim from the script. */
  text: string;
  /** 0-based storyboard index of the shot the line starts over (its anchor). */
  spanStartIndex: number;
  /**
   * 0-based index of the last shot the line plays across — the shot before the
   * next line's anchor; the final line runs to the last shot.
   */
  spanEndIndex: number;
  /**
   * NSB: the line carried an ENFORCED scene-anchored binding receipt, so the
   * span comes from the receipt, not the time-bucketing heuristic.
   */
  sceneBound?: boolean;
  /** NSB: the subject-alignment QC re-bound this line onto its evidence window. */
  rebound?: boolean;
  /** NSB: enforce-mode start shift (resolved − writer float), in seconds. */
  shiftSeconds?: number | null;
};

export type StoryScene = {
  key: string;
  index: number;
  sceneId: string | number | null;
  type: string | null;
  kindLabel: string;
  /** Prompt-aware animation flag (see isAnimatedScene) — not just the type. */
  animated: boolean;
  durationSeconds: number | null;
  prompt: string | null;
  titleText: string | null;
  sourceFile: string | null;
  transition: string | null;
  media?: EchoflicksSessionMedia;
  narration: StoryNarrationLine[];
  /** NWP-010: which planned narration window this shot sits in, if any. */
  window?: StoryWindow | null;
  i2v?: SceneI2VAudit | null;
};

/**
 * Planned per-scene start seconds from the envelope's `scene_timeline` — the
 * orchestrator's own timeline (real title-card durations, transition-overlap
 * modeling). Narration `start_second`s are authored against this plan, so
 * bucketing cues against it lands them on the shot the writer anchored; the
 * naive prompt-length sum (3 s title fallback, no overlaps) drifts by whole
 * scenes on crossfade-heavy echoes. Returns null unless EVERY script scene
 * resolves a timeline entry (id match first, position when the counts agree)
 * with monotone starts — anything less degrades to the naive sum, since a
 * half-matched timeline would break the partition.
 */
/**
 * NWP-010: per-shot view of the planned narration window. Returns null for a
 * legacy envelope, so the UI can say "no window data" rather than drawing a
 * chapter boundary between every pair of shots.
 */
function storyWindowFor(
  windowsByScene: Map<string, NarrationWindow>,
  sceneId: string | number | null,
): StoryWindow | null {
  if (sceneId == null) return null;
  const window = windowsByScene.get(String(sceneId));
  if (!window) return null;
  return {
    id: window.id,
    isAnchor: window.anchorSceneId === String(sceneId),
    isFirst: window.sceneIds[0] === String(sceneId),
    memberCount: window.sceneIds.length,
    oversized: window.oversized,
    boundaryLabel: boundaryLabel(window),
    beatIndex: window.beatIndex,
  };
}

function plannedAnchorStarts(
  envelope: ScriptEnvelope,
  scenes: ScriptScene[],
): number[] | null {
  const raw = Array.isArray(envelope.scene_timeline)
    ? envelope.scene_timeline
    : null;
  if (!raw || raw.length === 0 || scenes.length === 0) return null;

  const startsById = new Map<string, number>();
  const startsByPosition: number[] = [];
  for (const item of raw) {
    if (!isRecord(item)) return null;
    const start = item.start_second;
    if (typeof start !== "number" || !Number.isFinite(start)) return null;
    const id = item.id ?? item.scene_id;
    if (typeof id === "number" || (typeof id === "string" && id)) {
      startsById.set(String(id), start);
    }
    startsByPosition.push(start);
  }

  const starts: number[] = [];
  for (const [index, scene] of scenes.entries()) {
    const byId =
      scene.id != null ? startsById.get(String(scene.id)) : undefined;
    const start =
      byId ??
      (raw.length === scenes.length ? startsByPosition[index] : undefined);
    if (start == null) return null;
    starts.push(start);
  }
  // A non-monotone mapping means the timeline describes a different cut.
  for (let index = 1; index < starts.length; index += 1) {
    if (starts[index] < starts[index - 1]) return null;
  }
  return starts;
}

/**
 * Build a human-readable storyboard: one row per shot with its visual prompt,
 * the source photo, and the narration line anchored on it (with the span of
 * shots the line plays across).
 */
export function buildStoryboard(
  envelope: EnvelopeInput,
  media: EchoflicksSessionMedia[],
  /**
   * The effective I2V audit per scene, keyed by `scene_id`. When a scene's
   * `scene.id` is missing (older scripts), `buildStoryboard` falls back to the
   * positional index under the key `index:{n}`. Pass `undefined` (the default)
   * to keep today's planner-prompt-only behaviour.
   */
  sceneI2VByScene?: Map<string | number, SceneI2VAudit> | null,
): StoryScene[] {
  const env = envelope as ScriptEnvelope | undefined;
  if (!env) return [];
  const script = getDirectorScript(env);
  const scenes = script.video_content ?? [];
  const mediaByFilename = buildMediaMap(media);
  // NWP-010: real narration windows, or an empty map on a legacy envelope whose
  // one-window-per-scene shape says nothing about where the chapters are.
  const windowsByScene = windowBySceneId(readNarrationWindows(env));

  let cursor = 0;
  const rows = scenes.map((scene, index) => {
    const prompt = scene.video_prompt ?? {};
    const duration = durationForScene(scene);
    const start = cursor;
    const end = cursor + (duration ?? SCENE_FALLBACK_SECONDS);
    cursor = end;
    const sceneId = scene.id ?? null;
    const animated = isAnimatedScene(scene);
    return {
      row: {
        key: `${scene.id ?? index}-${scene.type ?? "scene"}`,
        index,
        sceneId,
        type: scene.type ?? null,
        kindLabel: sceneKindLabel(scene.type, animated),
        animated,
        durationSeconds: duration,
        prompt: prompt.prompt ?? prompt.generation_prompt ?? null,
        titleText: prompt.text ?? prompt.title_card_text ?? null,
        sourceFile: prompt.file ?? null,
        transition: scene.transition ?? null,
        media: prompt.file ? mediaByFilename.get(prompt.file) : undefined,
        narration: [] as StoryNarrationLine[],
        window: storyWindowFor(windowsByScene, sceneId),
        i2v: lookupSceneI2V(sceneI2VByScene, sceneId, index),
      } satisfies StoryScene,
      start,
      end,
    };
  });

  if (rows.length > 0) {
    const starts =
      plannedAnchorStarts(env, scenes) ?? rows.map((entry) => entry.start);

    // Scene starts partition the plan: a cue belongs to the last shot that has
    // started by its start_second (writers anchor lines at shot starts).
    const anchorIndexFor = (t: number): number => {
      let anchor = 0;
      for (let index = 0; index < starts.length; index += 1) {
        if (starts[index] <= t) anchor = index;
        else break;
      }
      return anchor;
    };

    const rowIndexBySceneId = new Map<string, number>();
    rows.forEach((entry, index) => {
      if (entry.row.sceneId != null) {
        rowIndexBySceneId.set(String(entry.row.sceneId), index);
      }
    });

    const cues: Array<{
      text: string;
      t: number;
      binding: Record<string, unknown> | null;
    }> = [];
    for (const cue of script.audio_content ?? []) {
      const line = stringValue(cue.voice_over_prompt);
      if (!line) continue;
      cues.push({
        text: line,
        t: cue.start_second ?? 0,
        binding: isRecord(cue.binding) ? cue.binding : null,
      });
    }
    // Authoring sorts by start_second, but old envelopes predate that validator.
    cues.sort((left, right) => left.t - right.t);

    // NSB: an ENFORCED binding receipt names the anchor scene explicitly — use
    // it instead of time-bucketing. Observe receipts document intent without
    // moving audio, so display keeps the heuristic (what actually plays).
    const enforcedAnchorIndex = (
      binding: Record<string, unknown> | null,
    ): number | null => {
      if (!binding || binding.mode !== "enforce") return null;
      const index = rowIndexBySceneId.get(String(binding.anchor_scene_id));
      return index ?? null;
    };
    const anchors = cues.map(
      (cue) => enforcedAnchorIndex(cue.binding) ?? anchorIndexFor(cue.t),
    );
    cues.forEach((cue, position) => {
      const spanStartIndex = anchors[position];
      const binding = cue.binding;
      const boundIndex = enforcedAnchorIndex(binding);
      let spanEndIndex =
        position + 1 < anchors.length
          ? Math.max(spanStartIndex, anchors[position + 1] - 1)
          : rows.length - 1;
      if (boundIndex != null && Array.isArray(binding?.scene_ids)) {
        const memberIndexes = (binding.scene_ids as unknown[])
          .map((sceneId) => rowIndexBySceneId.get(String(sceneId)))
          .filter((index): index is number => index != null);
        if (memberIndexes.length > 0) {
          spanEndIndex = Math.max(spanStartIndex, ...memberIndexes);
        }
      }
      const shift = binding?.shift_seconds;
      const line: StoryNarrationLine = {
        text: cue.text,
        spanStartIndex,
        spanEndIndex,
      };
      // Legacy cues keep the exact three-key shape; NSB annotations are
      // additive so old envelopes render byte-identically.
      if (boundIndex != null) {
        line.sceneBound = true;
        if (typeof shift === "number") line.shiftSeconds = shift;
      }
      if (binding?.source === "subject_qc_rebind") line.rebound = true;
      rows[spanStartIndex].row.narration.push(line);
    });
  }

  return rows.map((entry) => entry.row);
}

/**
 * Resolve a scene's I2V audit from the join map. Primary key is `scene_id`
 * (matched both as the raw value and its string form, since the manifest may
 * carry numeric ids while journey/media rows stringify them); positional
 * `index:{n}` is the fallback for scripts whose scenes have no id (gotcha #3).
 */
function lookupSceneI2V(
  byScene: Map<string | number, SceneI2VAudit> | null | undefined,
  sceneId: string | number | null,
  index: number,
): SceneI2VAudit | null {
  if (!byScene) return null;
  if (sceneId != null) {
    const direct = byScene.get(sceneId) ?? byScene.get(String(sceneId));
    if (direct) return direct;
  }
  return byScene.get(`index:${index}`) ?? null;
}

/**
 * The title-card shot, if the script has one — else the first shot that carries
 * title text. Powers the dedicated "The title" section (the on-screen title
 * card, distinct from the echo's name).
 */
export function findTitleScene(scenes: StoryScene[]): StoryScene | null {
  return (
    scenes.find((scene) => scene.type === "TitleCardScene") ??
    scenes.find((scene) => Boolean(scene.titleText)) ??
    null
  );
}

// ---------------------------------------------------------------------------
// "The cut": how the raw photos became the finished video
// ---------------------------------------------------------------------------

export type EchoCutSummary = {
  photosSent: number;
  photosUsed: number | null;
  shots: number;
  runtimeSeconds: number | null;
  narrationLines: number;
};

/** Headline stats for "the cut", derived from the already-built storyboard. */
export function summarizeCut(
  scenes: StoryScene[],
  media: EchoflicksSessionMedia[],
): EchoCutSummary {
  const photosSent = media.filter(
    (item) => item.kind === "uploaded_media",
  ).length;
  const usedPhotoIds = new Set<string | number>();
  let runtime = 0;
  let narrationLines = 0;
  for (const scene of scenes) {
    if (scene.media?.kind === "uploaded_media") {
      usedPhotoIds.add(scene.media.id);
    }
    runtime += scene.durationSeconds ?? 0;
    narrationLines += scene.narration.length;
  }
  return {
    photosSent,
    photosUsed: usedPhotoIds.size > 0 ? usedPhotoIds.size : null,
    shots: scenes.length,
    runtimeSeconds: runtime > 0 ? runtime : null,
    narrationLines,
  };
}

/** Aspect ratio in plain language for the look & sound chips. */
export function aspectLabel(aspect?: string | null): string | null {
  if (!aspect) return null;
  const map: Record<string, string> = {
    "16:9": "Landscape (16:9)",
    "9:16": "Vertical (9:16)",
    "4:3": "Classic (4:3)",
    "1:1": "Square (1:1)",
  };
  return map[aspect] ?? aspect;
}

// ---------------------------------------------------------------------------
// Voice & music summaries
// ---------------------------------------------------------------------------

export type VoiceSummary = { narrated: boolean; label: string };

export function summarizeVoice(
  script: DirectorScript | undefined,
  isNarrated?: boolean | null,
): VoiceSummary | null {
  const voice = script?.voice_generation;
  const narrated = Boolean(voice?.gen_type) || Boolean(isNarrated);
  if (!narrated) return null;
  if (voice?.gen_type === "predefined") {
    return {
      narrated: true,
      label: voice.voice_id
        ? `Narrated · ${voice.voice_id}`
        : "Narrated · preset voice",
    };
  }
  if (voice?.gen_type)
    return { narrated: true, label: "Narrated · custom voice" };
  return { narrated: true, label: "Narrated" };
}

/** A short, comma-joined music vibe from the global positive styles. */
export function summarizeMusic(
  script: DirectorScript | undefined,
  limit = 3,
): string | null {
  const styles = script?.music_prompt?.composition_plan?.positive_global_styles;
  if (!styles || styles.length === 0) return null;
  return styles.slice(0, limit).join(", ");
}

/**
 * The realized tempo of the ElevenLabs music track. ElevenLabs does not report
 * a BPM, so the composer measures it (librosa beat-track) during music QC and
 * emits it on the `music.qc.observed` event as `metadata.detected_bpm`, gated by
 * `metadata.beat_grid_stable`. We take the latest stable reading. Returns null
 * when no trustworthy BPM is available.
 */
export function detectedMusicBpm(
  events: EchoflicksSessionEvent[] | undefined,
): number | null {
  if (!events) return null;
  const observed = events
    .filter((event) => event.event_type === "music.qc.observed")
    .sort((left, right) =>
      String(left.occurred_at ?? left.id).localeCompare(
        String(right.occurred_at ?? right.id),
      ),
    );
  for (let index = observed.length - 1; index >= 0; index -= 1) {
    const payload = observed[index].raw_payload;
    const metadata =
      isRecord(payload) && isRecord(payload.metadata)
        ? payload.metadata
        : isRecord(payload)
          ? payload
          : null;
    if (!metadata || metadata.beat_grid_stable !== true) continue;
    const bpm = metadata.detected_bpm;
    if (typeof bpm === "number" && Number.isFinite(bpm) && bpm > 0) {
      return Math.round(bpm);
    }
  }
  return null;
}
